import React, { useCallback, useMemo, useState } from 'react';
import {
  Popconfirm,
  Form,
  Space,
  DatePicker,
  Col,
  Row,
  notification,
} from 'antd';
import { FormInstance } from 'antd/lib/form';
import { PlusOutlined } from '@ant-design/icons';
import { isNil, sumBy } from 'lodash';
import moment from 'moment';

import { toCurrency } from 'utils/utils';
import { dateToString, shouldUpdateCompare } from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import Value from 'components/Value';
import DataGrid from 'components/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import Title from 'components/Title';
import DeleteButton from 'components/DeleteButton';
import InputCurrency from 'components/InputCurrency';
import InputRate from 'components/InputRate';
import TextArea from 'antd/lib/input/TextArea';
import ISave from 'icons/ISave';
import {
  ActionsAssetpaymentschedule,
  AssetpaymentscheduleRequest,
} from 'services/assets';
import Button from 'components/Button';
import _ from 'lodash';
import GetLabel from 'DisplayFramework/components/GetLabel';

type TabAssetScheduleProps = {
  form: FormInstance;
  showAsModal?: boolean;
  isCreate?: boolean;
  assetId?: number;
  title?: boolean;
};

const TabAssetSchedule = (props: TabAssetScheduleProps) => {
  const { form, assetId, title } = props;

  const fieldName = 'assetschedules';

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'psid',
  });

  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: [],
    });
  }

  const [deletedItems, SetdeletedItems] = useState<any>([]);

  const addRow = () => {
    const totalAssetAmount: number = form.getFieldValue('assetnetamount');
    const totalPaymentsScheduled: number =
      form.getFieldValue('assetscheduledamount') +
      form.getFieldValue('totalHoldBackAmount');

    let assetscheduledamount = totalPaymentsScheduled
      ? totalAssetAmount - totalPaymentsScheduled
      : totalAssetAmount;
    assetscheduledamount = assetscheduledamount < 0 ? 0 : assetscheduledamount;
    const item: AssetpaymentscheduleRequest = {
      psid:Math.round(_.random(10000000)),
      paytype: 'ADVANCE',
      paymentdelaycode: '',
      previsionalduedate: moment() as any,
      paypercentage: assetscheduledamount / totalAssetAmount,
      payamount: assetscheduledamount,
      holdbackpercentage: 0,
      holdbackamount: 0,
      holdbackprevreleasedate: moment() as any,
      holdbackreleasedate: moment() as any,
      dueamount: assetscheduledamount,
      payduedate: moment() as any,
      paydate: moment() as any,
      paystatus: 'INIT',
      paycomment: '',
      action: 'ADD',
      assetid: assetId!,
      psorder: 0,
    };
    form.setFieldsValue({
      totalHoldBackAmount: 0,
      assetscheduledamount: assetscheduledamount,
    });

    add(item);
    recalculate(0, { payamount: assetscheduledamount });
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(fieldName);
      const totalAssetAmount: number = form.getFieldValue('assetnetamount');
      let newValues = values.map((item: any, i: any) => {
        if (i !== index) {
          return item;
        }

        const updatedItem = { ...item, ...updatedValues };

        const paypercentage = !isNil(updatedValues?.paypercentage)
          ? updatedItem.paypercentage
          : (updatedItem.payamount / totalAssetAmount) * 100;
        const payamount =
          updatedItem.paypercentage >= 0 && !isNil(updatedValues?.paypercentage)
            ? (updatedItem.paypercentage * totalAssetAmount) / 100
            : updatedItem.payamount;
        //holdback
        const holdbackpercentage =
          updatedItem.paytype === 'HOLDBACK'
            ? 100
            : updatedValues?.holdbackpercentage !== undefined
            ? updatedItem.holdbackpercentage
            : payamount !== 0
            ? (updatedItem.holdbackamount / payamount) * 100
            : 0;
        const holdbackamount = isNil(updatedValues.holdbackamount)
          ? (holdbackpercentage * payamount) / 100
          : updatedItem.holdbackamount;
        const dueamount = payamount - holdbackamount;

        return {
          ...updatedItem,
          payamount,
          paypercentage,
          dueamount,
          holdbackpercentage,
          holdbackamount,
        };
      });

      let assetscheduledamount = sumBy(newValues, 'payamount');
      if (assetscheduledamount > form.getFieldValue('assetnetamount')) {
        let amounttoreduce =
          assetscheduledamount - form.getFieldValue('assetnetamount');
        newValues = newValues.map((item: any, i: any) => {
          if (i === index) return item;

          if (item.payamount > amounttoreduce && amounttoreduce > 0) {
            const amount = amounttoreduce;
            amounttoreduce = 0;

            return {
              ...item,
              payamount: item.payamount - amount,
              dueamount: item.dueamount - amount,
              paypercentage:
                ((item.payamount - amount) / totalAssetAmount) * 100,
            };
          } else return { ...item };
        });
        assetscheduledamount = sumBy(newValues, 'payamount');
      }
      const totalHoldBackAmount = sumBy(newValues, 'holdbackamount');
      form.setFieldsValue({
        [fieldName]: newValues,
        assetscheduledamount,
        totalHoldBackAmount,
      });
    },
    [form]
  );

  const handleSaveLinesSchedule = async () => {
    const assetpaymentscheduleRequest: Array<AssetpaymentscheduleRequest> = form
      .getFieldValue(fieldName)
      ?.map((ele: any) => ({
        ...ele,
        holdbackprevreleasedate: dateToString(ele.holdbackprevreleasedate),
        holdbackreleasedate: dateToString(ele.holdbackreleasedate),
        paydate: dateToString(ele.paydate),
        payduedate: dateToString(ele.payduedate),
        previsionalduedate: dateToString(ele.previsionalduedate),
      }));
    if (
      form.getFieldValue('assetscheduledamount') +
        form.getFieldValue('totalHoldBackAmount') !==
        form.getFieldValue('assetnetamount') &&
      assetpaymentscheduleRequest.length !== 0
    ) {
      notification.error({
        message: 'Error validating input',
        description:
          'The total Payment Shcedule and the Total lines are different. please check',
      });
      return;
    }

    await ActionsAssetpaymentschedule({
      assetpaymentscheduleRequest:
        assetpaymentscheduleRequest.concat(deletedItems),
    }).then((res) => {
      notification.success({ message: res.message });
    });
  };

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        key: 'paytype',
        width: 150,
        fixed: 'left',
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'paytype']}>
            <ParListSelect
              parcode="APTYPE"
              disabled={record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'paymentdelaycode',
        key: 'paymentdelaycode',
        width: 180,
        render: (_, record, index) => (
          <Form.Item
            name={[fieldName, index, 'paymentdelaycode']}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="PAYDELAYIN"
              onChange={(value) => {
                let newDate = moment().add(
                  value.replace('M', '').substr(1, value.length),
                  'days'
                );
                if (value.indexOf('M') > -1) {
                  newDate = moment(newDate).endOf('month');
                }
                recalculate(index, {
                  previsionalduedate: newDate,
                });
              }}
              disabled={record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'previsionalduedate',
        key: 'previsionalduedate',
        width: 130,
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'previsionalduedate']}>
            <DatePicker
              value={moment(record.previsionalduedate)}
              disabled={record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'paypercentage',
        width: 140,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'paypercentage']}>
            <InputRate
              onChange={(value) => recalculate(index, { paypercentage: value })}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'payamount',
        width: 140,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'payamount']}>
            <InputCurrency
              currcode={form.getFieldValue('currcode')}
              onChange={(value) => recalculate(index, { payamount: value })}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'holdbackpercentage',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackpercentage']}>
            <InputRate
              onChange={(value) =>
                recalculate(index, { holdbackpercentage: value })
              }
              min={0}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'holdbackamount',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackamount']}>
            <InputCurrency
              currcode={form.getFieldValue('currcode')}
              onChange={(value) =>
                recalculate(index, { holdbackamount: value })
              }
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'holdbackprevreleasedate',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackprevreleasedate']}>
            <DatePicker
              value={moment(_record.holdbackprevreleasedate)}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'holdbackreleasedate ',
        key: 'holdbackreleasedate ',
        width: 130,
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackreleasedate ']}>
            {dateToString(record?.holdbackreleasedate)}
          </Form.Item>
        ),
      },
      {
        comcode: 'dueamount',
        key: 'dueamount',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'dueamount']}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode'))
              }
            />
          </Form.Item>
        ),
        width: 130,
      },
      {
        comcode: 'payduedate',
        key: 'payduedate',
        width: 130,
        render: (_, record, index) => {
          return (
            <Form.Item name={[fieldName, index, 'payduedate']}>
              <DatePicker
                value={moment(record.previsionalduedate)}
                disabled={record.paystatus === 'PAID'}
              />
            </Form.Item>
          );
        },
      },
      {
        comcode: 'paydate',
        key: 'paydate',
        width: 130,
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'paydate']}>
            {dateToString(record.paydate)}
          </Form.Item>
        ),
      },

      {
        comcode: 'paystatus',
        key: 'paystatus',
        width: 150,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'paystatus']}>
            <Value />
          </Form.Item>
        ),
      },
      {
        comcode: 'paycomment',
        key: 'paycomment',
        width: 200,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'paycomment']}>
            <TextArea disabled={_record.paystatus === 'PAID'} />
          </Form.Item>
        ),
      },

      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record, index) => (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              form.setFieldsValue({
                assetscheduledamount:
                  form.getFieldValue('assetscheduledamount') - record.payamount,
                totalHoldBackAmount:
                  form.getFieldValue('totalHoldBackAmount') -
                  record.holdbackamount,
              });
              if (record.action === 'UPDATE') {
                let newItems = deletedItems;
                newItems.push({ ...record, action: 'DELETE' });
                SetdeletedItems(newItems);
              }
              remove(record.psid);
            }}
          >
            <DeleteButton onlyIcon />
          </Popconfirm>
        ),
        align: 'center',
        fixed: 'right',
        width: 80,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recalculate]
  );

  const titles = (
    <>
      <Col span={16} />
      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare([
          'assetscheduledamount',
          'assetnetamount',
          'totalHoldBackAmount',
        ])}
      >
        {({ getFieldValue }) => {
          return (
            <Space size={16}>
              <span style={{ color: '#668cff' }}>
              <GetLabel label='Total Asset Amount :' /> {' '}
                {toCurrency(
                  getFieldValue('assetnetamount'),
                  getFieldValue('currcode')
                )}
              </span>
              <strong
                style={{
                  color:
                    getFieldValue('assetscheduledamount') +
                      getFieldValue('totalHoldBackAmount') ===
                    getFieldValue('assetnetamount')
                      ? 'green'
                      : getFieldValue('assetscheduledamount') +
                          getFieldValue('totalHoldBackAmount') >
                        getFieldValue('assetnetamount')
                      ? 'red'
                      : 'orange',
                }}
              >
                 <GetLabel label='Total Payments Scheduled:' style={{marginLeft:'20px'}}/> {' '}
            
                {toCurrency(
                  getFieldValue('assetscheduledamount'),
                  getFieldValue('currcode')
                )}
               <GetLabel label='Total HoldBack Scheduled:' style={{marginRight:'20px',marginLeft:'20px'}}/>  {' '}
                {toCurrency(
                  getFieldValue('totalHoldBackAmount'),
                  getFieldValue('currcode')
                )}
              </strong>
            </Space>
          );
        }}
      </Form.Item>
    </>
  );

  return (
    <div style={!title ? { backgroundColor: '#f7f7f7', padding: 5 } : {}}>
      {!title ? <Title comcode="PaymentSchedule" level={5}></Title> : ''}

      <div style={{ padding: 10, background: 'white' }}>
        <Row justify="end">
          <Col xs={20} />
          <Col xs={3} style={{ position: 'relative', left: '-7px' }}>
            <Button
              onClick={() => {
                handleSaveLinesSchedule();
              }}
              size="small"
              htmlType="button"
              comcode="Save"
              type="primary"
              icon={<ISave color="white" />}
            />
          </Col>
        </Row>

        <Form.Item noStyle shouldUpdate={shouldUpdateCompare('assetschedules')}>
          {({ getFieldValue }) => (
            <DataGrid
              title={titles}
              columns={columns}
              dataSource={getFieldValue(fieldName)}
              rowKey="psid"
              scroll={{ x: 1200, y: 360 }}
              hideFilters
              extraActions={
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  onClick={addRow}
                  comcode="add"
                />
              }
            />
          )}
        </Form.Item>
      </div>
    </div>
  );
};

export default TabAssetSchedule;
